import FocusedPageLayout from "@/layouts/FocusedPageLayout";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import classNames from "classnames";
import SkeletonElement from "./SkeletonElement";

interface MultiStepFormSkeletonProps {
  /** Number of steps to show in the stepper */
  stepCount?: number;
  /** Whether to show the stepper */
  renderStepper?: boolean;
  /** Whether to use full width layout */
  fullWidth?: boolean;
  /** Whether to show object heading skeleton */
  showObjectHeading?: boolean;
  /** Number of form sections to render */
  formSections?: number;
}

export default function MultiStepFormSkeleton({
  stepCount = 7,
  renderStepper = true,
  fullWidth = false,
  showObjectHeading = false,
  formSections = 5
}: MultiStepFormSkeletonProps) {
  const { isCompactLayout } = useIsCompactLayout();

  const cancelButton = (
    <div className="flex items-center">
      <SkeletonElement height="h-6" width="w-6" rounded="rounded-full" className="mr-3" />
      <SkeletonElement height="h-6" width="w-16" rounded="rounded-full" />
    </div>
  );

  const actions = (
    <div className="flex items-center gap-3">
      <SkeletonElement height="h-10" width="w-16" />
      <SkeletonElement height="h-10" width="w-20" />
    </div>
  );

  const renderDesktopStepper = () => (
    <div className="flex flex-col w-3/12 items-left">
      <div className="space-y-6 pl-1.5">
        {Array.from({ length: stepCount }).map((_, index) => (
          <div key={index} className="flex items-center gap-4">
            <SkeletonElement height="h-8" width="w-8" rounded="rounded-full" className="flex-shrink-0" />
            <div className="flex flex-col gap-2">
              <SkeletonElement height="h-4" width="w-24" rounded="rounded-full" />
              <SkeletonElement height="h-3" width="w-16" rounded="rounded-full" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderMobileStepper = () => (
    <div className="mb-6 -mx-4">
      <div className="flex items-center justify-between p-4 bg-sys-brand-surface">
        <div className="flex items-center gap-3">
          <SkeletonElement height="h-6" width="w-6" rounded="rounded-full" />
          <div className="flex flex-col gap-1">
            <SkeletonElement height="h-5" width="w-20" rounded="rounded-full" />
            <SkeletonElement height="h-4" width="w-16" rounded="rounded-full" />
          </div>
        </div>
        <SkeletonElement height="h-6" width="w-6" rounded="rounded-full" />
      </div>
    </div>
  );

  const renderFormContent = () => (
    <div className={classNames("flex flex-col", isCompactLayout ? "w-full" : "w-9/12")}>
      <div className="mb-8">
        <SkeletonElement height="h-8" width="w-48" rounded="rounded-full" className="mb-2" />
        <SkeletonElement height="h-5" width="w-24" rounded="rounded-full" />
      </div>

      <div className="space-y-8">
        {Array.from({ length: formSections }).map((_, sectionIndex) => (
          <div key={sectionIndex} className="space-y-6">
            <SkeletonElement height="h-6" width="w-32" rounded="rounded-full" />

            {sectionIndex === 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="space-y-2">
                    <SkeletonElement height="h-4" width="w-24" rounded="rounded-full" />
                    <SkeletonElement height="h-12" width="w-full" />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {isCompactLayout && (
        <div className="mt-8 flex justify-between">
          <SkeletonElement height="h-10" width="w-20" />
          <SkeletonElement height="h-10" width="w-16" />
        </div>
      )}
    </div>
  );

  return (
    <FocusedPageLayout stickyActions={true} cancelButton={cancelButton} actions={actions}>
      <div className={classNames("flex flex-col", fullWidth ? "lg:mx-[240px]" : "items-center")}>
        {showObjectHeading && (
          <div className={classNames("flex w-full justify-start px-4 pt-10 lg:px-0", !fullWidth && "max-w-[1024px]")}>
            <div className="flex items-center gap-4 w-full">
              <SkeletonElement height="h-16" width="w-16" rounded="rounded-full" />
              <div className="flex flex-col gap-2">
                <SkeletonElement height="h-8" width="w-64" rounded="rounded-full" />
                <SkeletonElement height="h-4" width="w-32" rounded="rounded-full" />
              </div>
              <div className="ml-auto flex gap-2">
                <SkeletonElement height="h-10" width="w-24" />
                <SkeletonElement height="h-10" width="w-20" />
              </div>
            </div>
          </div>
        )}

        <div
          className={classNames(
            "flex w-full flex-row gap-4 px-4 pt-2 lg:pt-16 lg:px-0",
            !fullWidth && "max-w-[1024px]",
            isCompactLayout && "flex-col"
          )}
          data-testid="multi-step-form-skeleton"
        >
          {renderStepper && (isCompactLayout ? renderMobileStepper() : renderDesktopStepper())}

          {renderFormContent()}
        </div>
      </div>
    </FocusedPageLayout>
  );
}
